const AUDIENCE_MATRIX_NUM_FIELDS = [
  'spend', 'impressions', 'clicks', 'reach', 'conversions', 'revenue',
  'add_to_cart', 'initiate_checkout', 'landing_page_views', 'engagements',
  'repeat_customers', 'first_time_purchase_count', 'returning_customer_purchase_count',
  'contribution_after_marketing',
];

const aggregateAudienceMatrixRows = (rows = []) => {
  if (!rows.length) return [];

  const grouped = new Map();

  rows.forEach((row) => {
    const key = row.adset_id || row.adset_name || '';
    if (!key) return;
    if (!grouped.has(key)) {
      const base = { ...row };
      AUDIENCE_MATRIX_NUM_FIELDS.forEach((field) => { base[field] = 0; });
      grouped.set(key, base);
    }
    const acc = grouped.get(key);
    AUDIENCE_MATRIX_NUM_FIELDS.forEach((field) => {
      acc[field] += Number(row[field]) || 0;
    });
    [
      'campaign_id', 'campaign_name', 'adset_id', 'adset_name', 'audience_type', 'geo_targeting',
      'country', 'city', 'age_range', 'gender', 'retargeting_window', 'lookalike_ratio',
      'adset_assessment',
    ].forEach((field) => {
      if (acc[field] == null && row[field] != null) acc[field] = row[field];
    });
  });

  return [...grouped.values()]
    .map((row) => {
      const spend = Number(row.spend) || 0;
      const impressions = Number(row.impressions) || 0;
      const clicks = Number(row.clicks) || 0;
      const conversions = Number(row.conversions) || 0;
      const revenue = Number(row.revenue) || 0;
      const addToCart = Number(row.add_to_cart) || 0;
      const initiateCheckout = Number(row.initiate_checkout) || 0;
      const firstTimePurchaseCount = Number(row.first_time_purchase_count) || 0;

      return {
        ...row,
        roas: spend > 0 ? Number((revenue / spend).toFixed(3)) : 0,
        ctr: impressions > 0 ? Number(((clicks / impressions) * 100).toFixed(2)) : 0,
        cpm: impressions > 0 ? Number(((spend / impressions) * 1000).toFixed(2)) : 0,
        cpc: clicks > 0 ? Number((spend / clicks).toFixed(2)) : null,
        cac: firstTimePurchaseCount > 0 ? Number((spend / firstTimePurchaseCount).toFixed(2)) : null,
        add_to_cart_rate_from_clicks: clicks > 0 ? Number(((addToCart / clicks) * 100).toFixed(3)) : 0,
        checkout_rate_from_clicks: clicks > 0 ? Number(((initiateCheckout / clicks) * 100).toFixed(3)) : 0,
        purchase_rate_from_clicks: clicks > 0 ? Number(((conversions / clicks) * 100).toFixed(3)) : 0,
        revenue_per_click: clicks > 0 ? Number((revenue / clicks).toFixed(2)) : 0,
        revenue_per_1000_impressions: impressions > 0 ? Number(((revenue / impressions) * 1000).toFixed(2)) : 0,
        contribution_after_marketing: Number((revenue - spend).toFixed(2)),
      };
    })
    .sort((a, b) => (Number(b.spend) || 0) - (Number(a.spend) || 0));
};

export const deriveAudienceMatrixFromRawRange = (entries = []) => {
  const rows = entries.flatMap((entry) => Array.isArray(entry?.rows) ? entry.rows : []);
  const aggregatedRows = aggregateAudienceMatrixRows(rows);
  if (!aggregatedRows.length) return null;

  return {
    totals: {
      adset_count: aggregatedRows.length,
      spend: Number(aggregatedRows.reduce((sum, row) => sum + (Number(row.spend) || 0), 0).toFixed(2)),
      revenue: Number(aggregatedRows.reduce((sum, row) => sum + (Number(row.revenue) || 0), 0).toFixed(2)),
      conversions: aggregatedRows.reduce((sum, row) => sum + (Number(row.conversions) || 0), 0),
    },
    rows: aggregatedRows,
  };
};
